import { useState, useCallback, useEffect, useRef } from "react";

import { config } from "../../config";
import { useHttpClient } from "../Hooks/http-hook";
import dedupeStoreFetch from "../util/dedupe-store-fetch";

const themeDefault = {
    mainColor: '#1A1A1A',
    secondaryColor: '#83ccdd',
}

const useTheme = props => {
    const [theme, setTheme] = useState(themeDefault);

    const [isUpdatedTheme, setIsUpdatedTheme] = useState(false);
    const [HideThemeSkeleton, setHideThemeSkeleton] = useState(false);
    const [themeStoreUsername, setThemeStoreUsername] = useState('');
    const { error, sendRequest, clearError } = useHttpClient();
    const cacheRef = useRef({
        isUpdatedTheme: false,
        themeStoreUsername: '',
        theme: themeDefault,
    });
    const pendingFetchesRef = useRef(new Map());

    cacheRef.current = {
        isUpdatedTheme,
        themeStoreUsername,
        theme,
    };

    const updateTheme = (newTheme) => {
        setTheme((prev) => ({ ...prev, ...newTheme }));
        setIsUpdatedTheme(true);
    };

    const fetchTheme = useCallback(async (username, { force = false } = {}) => {
        return dedupeStoreFetch({
            key: username,
            force,
            cacheRef,
            pendingRef: pendingFetchesRef,
            isCacheHit: (cache) => (
                cache.isUpdatedTheme && cache.themeStoreUsername === username
            ),
            getCachedValue: (cache) => cache.theme,
            runRequest: async () => {
                try {
                    const responseData = await sendRequest(
                        `${config.API_URL}/theme/${username}`
                    );
                    clearError();
                    updateTheme(responseData);
                    setThemeStoreUsername(username);
                    return responseData;
                } catch (err) {
                    setIsUpdatedTheme(true);
                    setThemeStoreUsername(username);
                    console.error(err);
                    return null;
                }
            },
        });
    }, [sendRequest, clearError]);

    const updateDOMColors = useCallback((newTheme={}) => {
        document.documentElement.style.setProperty('--app-main-color', `${newTheme?.mainColor || theme.mainColor}`);
        document.documentElement.style.setProperty('--app-second-color', `${newTheme?.secondaryColor || theme.secondaryColor}`);
    }, [theme]);

    const reloadTheme = useCallback(
        (username) => fetchTheme(username, { force: true }),
        [fetchTheme]
    );

    const removeTheme = useCallback(() => {
        pendingFetchesRef.current.clear();
        setTheme(themeDefault);
        updateDOMColors(themeDefault);
        setIsUpdatedTheme(false);
        setThemeStoreUsername('');
        setHideThemeSkeleton(false);
    }, [updateDOMColors, setHideThemeSkeleton]);

    useEffect(() => {
        const storedData = JSON.parse(localStorage.getItem('storec-userData'));
        if (storedData && storedData.username && props.role === "store" && !isUpdatedTheme) {
            fetchTheme(storedData.username);
            setTimeout(() => {
                setHideThemeSkeleton(true);
            }, 500);
        }
    }, [props.role]);

    useEffect(() => {
        updateDOMColors(theme);
    }, [theme]);

    return {
        theme,
        setTheme,
        isUpdatedTheme, 
        fetchTheme,
        reloadTheme,
        HideThemeSkeleton,
        ThemeError: error,
        removeTheme,
        setHideThemeSkeleton,
        updateDOMColors,
        themeStoreUsername,
    };
}

export default useTheme;
